import { PageContainer } from '@ant-design/pro-layout'
import { makeStyles } from '@material-ui/styles'
import { Col, Collapse, Divider, Modal, Row, Statistic } from 'antd'
import _ from 'lodash'
import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useReactToPrint } from 'react-to-print'

import Card from '@/components/Card'
import DurationFilterButton from '@/components/DurationFilterButton'
import FilterDialog from '@/components/FilterDialog'
import LoadingIndicator from '@/components/LoadingIndicator'
import ReportActionButtons from '@/components/ReportActionButtons'
import ReportPrintContainer from '@/components/ReportPrintContainer'
import { pageStyle } from '@/constants'
import dimorderApi from '@/libs/api/dimorderApi'
import { currencyWithCommas } from '@/libs/numberWithCommas'
import { useSelector } from '@/redux'
import colors from '@/style/colors'

import SummaryInfo from './Summary/SummaryInfo'

export default function DailyClosing(props) {
  const { t } = useTranslation()
  const printRef = useRef()
  const classes = useStyles(props)
  const { Panel } = Collapse
  const [params, setParams] = React.useState(null)
  const [dailyClosing, setDailyClosing] = React.useState(null)

  const [loading, setLoading] = React.useState(false)
  const [activeKey, setActiveKey] = React.useState('1')
  const activeMerchant = useSelector((state) => state.merchant.activeMerchant)

  React.useEffect(() => {
    if (params) {
      const getItems = async () => {
        setLoading(true)
        try {
          const dailyClosing = await dimorderApi
            .instance(activeMerchant.id)
            ?.getDailyClosing(params)
          const tableGroupSalesSection = await dimorderApi
            .instance(activeMerchant.id)
            ?.getTableGroupSalesSection(params)
          const combinedSections = [...(dailyClosing?.sections ?? []), tableGroupSalesSection] // 將不同 API 來源的資料整合成一個 sections，注意順序有其必要性
          setDailyClosing({ sections: combinedSections })
          setActiveKey('0')
        } catch (error) {
          if (error.response.status === 400 && error.response.data.error === 'too many orders') {
            Modal.error({
              title: t('app.common.error'),
              content: t('page.dailyClosing.exceedLimitError'),
            })
          } else {
            Modal.error({
              title: t('app.common.error'),
              content: t('app.common.submitError'),
            })
          }
          setLoading(false)
          setActiveKey('1')
        }
        setLoading(false)
      }
      getItems()
    }
  }, [params, t, activeMerchant])

  const sectionsByType = _.keyBy(dailyClosing?.sections, 'type')
  const { sales: salesSection, table: tableSection } = sectionsByType

  const displayResult = () => (
    <Row gutter={16} className={classes.container}>
      <Col xl={18} xs={24}>
        <Card
          width='100%'
          height='100%'
          borderRadius='2px'
          textAlign='left'
          padding='20px'
          boxShadow='1px 1px  5px rgba(0, 0, 0, 0.3)'
        >
          <Row style={{ textAlign: 'center' }}>
            <Col xl={12} xs={24} className={classes.positioner}>
              <Statistic
                title={t('page.dailyClosing.merchantTotalIncome')}
                value={currencyWithCommas(
                  salesSection?.data?.totalSales,
                  activeMerchant?.setting?.currency,
                  activeMerchant?.rounding?.minFractionDigits,
                )}
                valueStyle={{ fontSize: '2.5rem' }}
              />
            </Col>
            <Col xl={12} xs={24} className={classes.positioner}>
              <Statistic
                title={t('page.dailyClosing.transaction')}
                value={`${salesSection?.data?.sales?.count}`}
                valueStyle={{ fontSize: '2.5rem' }}
              />
            </Col>
          </Row>
          <Row style={{ textAlign: 'center' }}>
            <Col xl={12} xs={24} className={classes.positioner}>
              <Statistic
                title={t('page.dailyClosing.cancelledOrder')}
                value={`${salesSection?.data?.cancelled?.count}`}
                valueStyle={{ fontSize: '2.5rem' }}
              />
            </Col>
            <Col xl={12} xs={24} className={classes.positioner}>
              <Statistic
                title={t('page.dailyClosing.tableGuest')}
                value={`${tableSection?.data?.guest}`}
                valueStyle={{ fontSize: '2.5rem' }}
              />
            </Col>
          </Row>
          <Divider className={classes.divider} />
          <br />
          <SummaryInfo data={dailyClosing} />
        </Card>
      </Col>
    </Row>
  )

  const printResult = () => (
    <ReportPrintContainer
      printRef={printRef}
      start={params?.start}
      end={params?.end}
      title={t('page.dailyClosing.title')}
    >
      <Row gutter={16} className={classes.container}>
        <Col span={18}>
          <>
            <Row style={{ textAlign: 'center' }}>
              <Col span={12} className={classes.positioner}>
                <Statistic
                  title={t('page.dailyClosing.merchantTotalIncome')}
                  value={currencyWithCommas(
                    salesSection?.data?.revenue,
                    activeMerchant?.setting?.currency,
                    activeMerchant?.rounding?.minFractionDigits,
                  )}
                  valueStyle={{ fontSize: '2.5rem' }}
                />
              </Col>
              <Col span={12} className={classes.positioner}>
                <Statistic
                  title={t('page.dailyClosing.transaction')}
                  value={`${salesSection?.data?.sales?.count}`}
                  valueStyle={{ fontSize: '2.5rem' }}
                />
              </Col>
            </Row>
            <Row style={{ textAlign: 'center' }}>
              <Col span={12} className={classes.positioner}>
                <Statistic
                  title={t('page.dailyClosing.cancelledOrder')}
                  value={`${salesSection?.data?.cancelled?.count}`}
                  valueStyle={{ fontSize: '2.5rem' }}
                />
              </Col>
              <Col span={12} className={classes.positioner}>
                <Statistic
                  title={t('page.dailyClosing.tableGuest')}
                  value={`${tableSection?.data?.guest}`}
                  valueStyle={{ fontSize: '2.5rem' }}
                />
              </Col>
            </Row>
            <Divider className={classes.divider} />
            <br />
            <SummaryInfo data={dailyClosing} />
          </>
        </Col>
      </Row>
    </ReportPrintContainer>
  )

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    pageStyle,
  })

  const handleExport = async () => {
    try {
      await dimorderApi.instance(activeMerchant.id)?.exportDailyClosingCsv(params)
    } catch (error) {
      console.log(error)
      Modal.error({
        title: t('app.common.error'),
        content: t('app.common.exportError'),
      })
    }
  }

  return (
    <PageContainer
      ghost
      header={{
        title: t('page.dailyClosing.title'),
      }}
    >
      <Collapse
        ghost
        expandIcon={() => <></>}
        activeKey={activeKey}
        onChange={(key) => setActiveKey(key)}
      >
        <Panel
          key='1'
          style={{ fontSize: 22 }}
          extra={
            <DurationFilterButton start={params?.start} end={params?.end} activeKey={activeKey} />
          }
        >
          <FilterDialog setParams={setParams} momentFormatX />
        </Panel>
      </Collapse>
      <br />
      <LoadingIndicator loading={loading} />
      {dailyClosing && !loading && (
        <>
          <ReportActionButtons onPrint={handlePrint} onExport={handleExport} />
          {displayResult()}
          {printResult()}
        </>
      )}
    </PageContainer>
  )
}

const useStyles = makeStyles(() => ({
  positioner: {
    paddingTop: 20,
  },
  divider: {
    borderColor: colors.dividerDark,
  },
  container: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: 20,
  },
}))
